import {EventEmitter, Injectable} from '@angular/core';
import {ExcTranslation} from 'exc-core-v2';
import {ExcSecurityService} from 'exc-security-v2';
import {ExcRestService} from 'exc-rest-v2';
import {loadCldr, setCulture, setCurrencyCode} from '@syncfusion/ej2-base';


import * as currencies from 'cldr-data/main/de/currencies.json';
import * as timeZoneNames from 'cldr-data/main/de/timeZoneNames.json';
import * as numbers from 'cldr-data/main/de/numbers.json';
import * as gregorian from 'cldr-data/main/de/ca-gregorian.json';
import * as currencyData from 'cldr-data/supplemental/currencyData.json';
import * as numberingSystems from 'cldr-data/supplemental/numberingSystems.json';

@Injectable({
  providedIn: 'root'
})
export class EnumerationService extends ExcTranslation {

  loading = true;
  repositoryLoaded: EventEmitter<any> = new EventEmitter<any>();

  constructor(public excSecurityService: ExcSecurityService,
              private excRestService: ExcRestService) {
    super();
    this.registerEvents();
  }

  registerEvents(): void {
    this.excSecurityService.OnTokenRefreshed.subscribe(() => {
      this.getRepository();
    });
    this.onLangChange().subscribe(() => {
      this.getRepository();
    });
    this.repositoryLoaded.subscribe(() => {
      this.setCulture(localStorage.getItem('appLanguage') || 'de', 'EUR');
    });
  }

  rowSort(obj1: any , obj2: any): number | void {
    const sortColumn = 'enmSort';
    if (obj1[sortColumn] || obj2[sortColumn]) {
      return obj1[sortColumn] - obj2[sortColumn];
    }
    return;
  }

  getRepository(): void {
    this.loading = true;
    if (this.excSecurityService.LoggedIn) {
      this.excRestService.get(this.excRestService.computeUrl('RepositoryR_Summary',
        {appLanguage: localStorage.getItem('appLanguage')}))
        .subscribe((response: any) => {
          if (response.success) {
            const listName = 'List';
            // Enumerations
            const obj: any = {};
            for (const o of response.data.Enumeration) {
              if (!obj[o.enmName]) {
                obj[o.enmName] = {} as any;
                obj[o.enmName][listName] = response.data.Enumeration.filter((p: any) => p.enmName === o.enmName).sort(this.rowSort);
              }
              if (!obj[o.enmName][o.enmIdent]) {
                obj[o.enmName][o.enmIdent] = o;
              }
            }
            this.translateService.resources.Enumerations = obj;

            // FieldRepository
            const obj2: any = {};
            for (const o of response.data.FieldRepository) {
              if (!obj2[o.repTable]) {
                obj2[o.repTable] = {} as any;
                // obj2[o.repTable][listName] = response.data.FieldRepository.filter((p: any) => p.repTable === o.repTable);
              }
              if (!obj2[o.repTable][o.repColumn]) {
                obj2[o.repTable][o.repColumn] = o;
              }
            }
            this.translateService.resources.Entities = obj2;

            // DokEigenschaftTyp
            const obj3: any = {};
            for (const o of response.data.DokEigenschaftTyp) {
              if (!obj3[o.det_enmIdent_DokTyp]) {
                obj3[o.det_enmIdent_DokTyp] = [] as any[];
              }
              obj3[o.det_enmIdent_DokTyp].push(o);
            }
            this.translateService.resources.DokEigenschaftTyp = obj3;
          }
          setTimeout(() => {
            this.loading = false;
            this.repositoryLoaded.emit();
          }, 500);
        });
    }
  }

  getEnumerationList(enmName: string, includeInvalid = false): any[] {
    const rt: any = this.getTranslation('Enumerations.' + enmName + '.List');
    if (rt !== 'Enumerations.' + enmName + '.List') {
      return (!includeInvalid) ? rt.filter((e: any) => {
        return (e.enmVFR <= new Date().getTime() && e.enmVTO >= new Date().getTime()) && (e.enmValid === undefined || e.enmValid);
      }) : this.getTranslation('Enumerations.' + enmName + '.List');
    }
    return [];
  }

  getEnumerationItem(enmName: string, enmIdent: number): any {
    const num: number = +enmIdent;
    const rt = this.getEnumerationList(enmName, true);
    if (rt.length > 0) {
      return rt.find((e: any) => {
        return e.enmIdent === num;
      });
    }
    return undefined;
  }

  getEnumerationText(enmName: string, enmIdent: number, emptyStringOnMissing:boolean = false): any {
    const rt = this.getEnumerationItem(enmName, enmIdent);
    if (rt) {
      return rt.enmText;
    }
    return (this.loading || emptyStringOnMissing) ? '' : 'Missing Enumeration: ' + enmName + '.' + enmIdent;
  }

  getEnumerationTextA(enmName: string, enmIdent: number, emptyStringOnMissing:boolean = false): any {
    const rt = this.getEnumerationItem(enmName, enmIdent);
    if (rt) {
      return rt.enmTextA;
    }
    return (this.loading || emptyStringOnMissing) ? '' : 'Missing Enumeration: ' + enmName + '.' + enmIdent;
  }

  getEnumerationDefaultItem(enmName: string): any {
    let rt;
    rt = this.getEnumerationList(enmName).find(e => e.enmDefault === true);
    if (!rt) {
      rt = this.getEnumerationList(enmName)[0];
    }
    return rt;
  }

  getEnumerationDefaultValue(enmName: string): any {
    return this.getEnumerationDefaultItem(enmName).enmIdent;
  }

  getFieldItem(repTable: string, repColumn: string): any {
    const rt = this.getTranslation('Entities.' + repTable + '.' + repColumn);
    if (rt !== 'Entities.' + repTable + '.' + repColumn) {
      return rt;
    }
    return undefined;
  }

  getFields(repTable: string): any {
    const rt = this.getTranslation('Entities.' + repTable);
    if (rt !== 'Entities.' + repTable) {
      return rt;
    }
    return [];
  }

  getFieldCaption(repTable: string, repColumn: string): string {
    const rt = this.getFieldItem(repTable, repColumn);
    if (rt) {
      return rt.repText;
    }
    return repColumn;
  }

  getFieldCaptionA(repTable: string, repColumn: string): string {
    const rt = this.getFieldItem(repTable, repColumn);
    if (rt) {
      return rt.repTextA;
    }
    return repColumn;
  }

  getDokEigenschaftTypList(dokTyp: number): any[] {
    const typelist: any = this.getTranslation('DokEigenschaftTyp');
    if (typelist && typelist[dokTyp]) {
      return typelist[dokTyp];
    }
    return [];
  }

  getMIMEIcon(mime: string): string {
    const MIMES: any = this.getTranslation('Icons.MIME');
    let value = 'far fa-file';
    for (const key of Object.keys(MIMES)) {
      if (mime.indexOf(key) === 0) {
        value = MIMES[key];
        break;
      }
    }
    return value;
  }

  setCulture(lang: string, cc: string | null): void {
    setCulture(lang || 'de');
    setCurrencyCode(cc || 'EUR');
    loadCldr();

    loadCldr(gregorian, timeZoneNames, numbers, numberingSystems, currencies, currencyData);
  }
}
